import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

function useMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
}

export const FORMAT_OPTIONS = {
  // Option bloquée pour 2 tireurs : toujours Finale directe
  2:  [{ label: "Finale directe", value: "finale_directe", nb: 1 }],
  3:  [{ label: "Poule Unique (3)", value: "poule_unique", nb: 1 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  4:  [{ label: "Poule Unique (4)", value: "poule_unique", nb: 1 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  5:  [{ label: "Poule Unique (5)", value: "poule_unique", nb: 1 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  6:  [{ label: "2 Poules de 3", value: "poules", nb: 2 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  7:  [{ label: "2 Poules (3+4)", value: "poules", nb: 2 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  8:  [{ label: "2 Poules de 4", value: "poules", nb: 2 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  9:  [{ label: "3 Poules de 3", value: "poules", nb: 3 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  10: [{ label: "3 Poules (3+3+4)", value: "poules", nb: 3 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  11: [{ label: "3 Poules (4+4+3)", value: "poules", nb: 3 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  12: [{ label: "3 Poules de 4", value: "poules", nb: 3 }, { label: "4 Poules de 3", value: "poules", nb: 4 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  13: [{ label: "4 Poules (3+3+3+4)", value: "poules", nb: 4 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  14: [{ label: "4 Poules (3+3+4+4)", value: "poules", nb: 4 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  15: [{ label: "4 Poules (3+4+4+4)", value: "poules", nb: 4 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }],
  16: [{ label: "4 Poules de 4", value: "poules", nb: 4 }, { label: "Élimination directe", value: "elimination_directe", nb: 1 }]
};

// Valeurs par défaut recommandées
export const DEFAULT_FORMATS = {
  3: { format: 'poule_unique', nb: 1 },
  4: { format: 'poule_unique', nb: 1 },
  5: { format: 'poule_unique', nb: 1 },
  6: { format: 'poules', nb: 2 },
  7: { format: 'poules', nb: 2 },
  8: { format: 'poules', nb: 2 },
  9: { format: 'poules', nb: 3 },
  10: { format: 'poules', nb: 3 },
  11: { format: 'poules', nb: 3 },
  12: { format: 'poules', nb: 3 },
  13: { format: 'poules', nb: 4 },
  14: { format: 'poules', nb: 4 },
  15: { format: 'poules', nb: 4 },
  16: { format: 'poules', nb: 4 }
};

export default function SettingsManager({ settings = {}, onUpdate, readOnly = false }) {
  const isMobile = useMobile();
  const [localSettings, setLocalSettings] = useState(settings);

  // On synchronise le local si les settings parent changent (ex: après un refresh)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const sameRingPerCategory = localSettings.__sameRingPerCategory !== false;
  const useSeeding = localSettings.__useSeeding === true;

  const handleChange = (num, format, nb) => {
    setLocalSettings(prev => ({
      ...prev,
      [num]: { format, nb }
    }));
  };

  const handleToggleSameRing = () => {
    setLocalSettings(prev => ({
      ...prev,
      __sameRingPerCategory: !sameRingPerCategory
    }));
  };

  const handleToggleSeeding = () => {
    setLocalSettings(prev => ({
      ...prev,
      __useSeeding: !useSeeding
    }));
  };

  const handleSave = () => {
    if (readOnly) return;
    // On passe tout l'objet local au parent (App.js) pour la sauvegarde Supabase
    onUpdate(localSettings); 
    Swal.fire({
      icon: 'success',
      title: 'Configuration sauvegardée',
      text: 'Les formats ont été mis à jour avec succès.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleReset = () => {
    if (readOnly) return;
    Swal.fire({
      title: 'Réinitialiser tout ?',
      text: "Tous les formats repasseront en Élimination Directe.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        const resetData = {};
        Object.keys(FORMAT_OPTIONS).forEach(num => {
          // On cherche l'option élimination dans la liste, sinon poule_unique par défaut
          const elimOpt = FORMAT_OPTIONS[num].find(o => o.value === 'elimination_directe') || FORMAT_OPTIONS[num][0];
          resetData[num] = { format: elimOpt.value, nb: elimOpt.nb };
        });
        setLocalSettings(resetData);
        onUpdate(resetData);
      }
    });
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.header, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? '15px' : undefined }}>
        <div>
          <h2 style={styles.title}>Configuration des Formats</h2>
          <p style={styles.subtitle}>Définissez comment chaque catégorie doit être découpée.</p>
        </div>
        <div style={{ ...styles.btnGroup, flexDirection: isMobile ? 'column' : 'row' }}>
          <button onClick={handleReset} disabled={readOnly} style={{...styles.btnReset, opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer'}}>Réinitialiser</button>
          <button onClick={handleSave} disabled={readOnly} style={{...styles.btnSave, backgroundColor: readOnly ? '#a0aec0' : '#3182ce', cursor: readOnly ? 'not-allowed' : 'pointer'}}>{readOnly ? 'Configuration verrouillée' : 'Enregistrer les modifications'}</button>
        </div>
      </div>

      {/* OPTION : même ring par catégorie */}
      <div style={{ ...styles.optionRow, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center' }}>
        <div>
          <div style={styles.optionLabel}>Poules d'une même catégorie sur la même enceinte</div>
          <div style={styles.optionDesc}>
            {sameRingPerCategory
              ? "Les poules d'une même catégorie restent sur le même ring. L'algorithme choisit le ring le moins chargé par catégorie."
              : "Chaque poule est affectée individuellement au ring le moins chargé, sans contrainte de catégorie."}
          </div>
        </div>
        <button
          onClick={!readOnly ? handleToggleSameRing : undefined}
          disabled={readOnly}
          style={{
            ...styles.toggle,
            backgroundColor: sameRingPerCategory ? '#3182ce' : '#cbd5e0',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            opacity: readOnly ? 0.5 : 1
          }}
          aria-pressed={sameRingPerCategory}
        >
          <span style={{
            ...styles.toggleKnob,
            transform: sameRingPerCategory ? 'translateX(22px)' : 'translateX(2px)'
          }} />
        </button>
      </div>

      {/* OPTION : têtes de série */}
      <div style={{ ...styles.optionRow, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center' }}>
        <div>
          <div style={styles.optionLabel}>Respecter le classement (têtes de série)</div>
          <div style={styles.optionDesc}>
            {useSeeding
              ? "Les têtes de série n°1 et n°2 sont placées dans des poules opposées et ne peuvent se rencontrer qu'en finale."
              : "Les athlètes sont répartis aléatoirement sans tenir compte du classement."}
          </div>
        </div>
        <button
          onClick={!readOnly ? handleToggleSeeding : undefined}
          disabled={readOnly}
          style={{
            ...styles.toggle,
            backgroundColor: useSeeding ? '#3182ce' : '#cbd5e0',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            opacity: readOnly ? 0.5 : 1
          }}
          aria-pressed={useSeeding}
        >
          <span style={{
            ...styles.toggleKnob,
            transform: useSeeding ? 'translateX(22px)' : 'translateX(2px)'
          }} />
        </button>
      </div>

      <div style={styles.grid}>
        {Object.keys(FORMAT_OPTIONS).map(num => {
          const currentConfig = localSettings[num] || { format: 'poule_unique', nb: 1 };
          const selectValue = `${currentConfig.format}_${currentConfig.nb}`;

          return (
            <div key={num} style={styles.settingRow}>
              <span style={styles.countBadge}>{num} tireurs</span>
              <select 
                value={selectValue}
                disabled={readOnly}
                onChange={(e) => {
                  const parts = e.target.value.split('_');
                  const nb = parts.pop(); 
                  const format = parts.join('_'); 
                  handleChange(num, format, parseInt(nb, 10));
                }}
                style={{...styles.select, backgroundColor: readOnly ? '#f7fafc' : 'white', cursor: readOnly ? 'not-allowed' : 'pointer'}}
              >
                {FORMAT_OPTIONS[num].map(opt => (
                  <option 
                    key={`${opt.value}_${opt.nb}`} 
                    value={`${opt.value}_${opt.nb}`}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: { padding: '30px', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '25px', borderBottom: '1px solid #edf2f7', paddingBottom: '20px' },
  title: { margin: 0, color: '#2d3748', fontSize: '22px', fontWeight: 'bold' },
  subtitle: { margin: '5px 0 0 0', color: '#718096', fontSize: '14px' },
  btnGroup: { display: 'flex', gap: '12px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' },
  settingRow: { 
    padding: '15px', border: '1px solid #edf2f7', borderRadius: '12px', 
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' 
  },
  countBadge: { fontWeight: 'bold', color: '#2c5282', fontSize: '14px' },
  select: { 
    padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', 
    cursor: 'pointer', backgroundColor: 'white', outline: 'none', minWidth: '150px' 
  },
  btnSave: { padding: '10px 20px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'background 0.2s' },
  btnReset: { padding: '10px 20px', backgroundColor: 'transparent', color: '#e53e3e', border: '1px solid #e53e3e', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  optionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', padding: '16px 20px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '20px' },
  optionLabel: { fontWeight: '600', fontSize: '14px', color: '#2d3748', marginBottom: '4px' },
  optionDesc: { fontSize: '12px', color: '#718096' },
  toggle: { flexShrink: 0, width: '48px', height: '26px', borderRadius: '13px', border: 'none', position: 'relative', transition: 'background 0.2s' },
  toggleKnob: { position: 'absolute', top: '3px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'transform 0.2s', display: 'block' }
};